import { z } from "zod";

import { LessonIdSchema } from "./identifiers.js";

const Sha256Schema = z.string().regex(/^[a-f0-9]{64}$/u);
const RepositoryPathSchema = z
  .string()
  .min(1)
  .refine(
    (value) =>
      !value.startsWith("/") &&
      !/^[a-z]:/iu.test(value) &&
      !value.split(/[\\/]/u).includes(".."),
    "Publication paths must be repository-relative",
  );

export const CanonicalPublicationLessonSchema = z.object({
  lessonId: LessonIdSchema,
  lessonPath: RepositoryPathSchema,
  lessonSha256: Sha256Schema,
  reviewArtifacts: z
    .array(
      z.object({
        path: RepositoryPathSchema,
        sha256: Sha256Schema,
      }),
    )
    .min(1),
});

export const CanonicalPublicationManifestSchema = z
  .object({
    schema: z.literal("mathos.v2.canonical_publication_manifest.v1"),
    publicationId: z.string().regex(/^PUB-[a-f0-9]{24}$/u),
    createdAt: z.string().datetime({ offset: true }),
    canonicalAudit: z.object({
      path: RepositoryPathSchema,
      sha256: Sha256Schema,
    }),
    releaseLessonIds: z.array(LessonIdSchema).min(1),
    lessons: z.array(CanonicalPublicationLessonSchema).min(1),
    contentSetSha256: Sha256Schema,
    releaseChannel: z
      .enum(["commercial", "household_noncommercial"])
      .default("commercial"),
    reviewAuthority: z
      .enum(["dual_human", "ai_pro_owner_authorized"])
      .default("dual_human"),
    commercialReuseAllowed: z.boolean().default(true),
    requiresHumanCanonicalApprovalForCommercial: z.boolean().default(false),
    reviewAuthorityArtifact: z
      .object({
        authorityKind: z.enum(["single", "set"]).optional(),
        authorityId: z.string().regex(/^HRA-[A-Za-z0-9._-]+$/u),
        path: RepositoryPathSchema,
        sha256: Sha256Schema,
        modelSlug: z.string().regex(/-pro$/u),
      })
      .nullable()
      .default(null),
  })
  .superRefine((manifest, context) => {
    if (
      manifest.publicationId !==
      `PUB-${manifest.contentSetSha256.slice(0, 24)}`
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["publicationId"],
        message: "Publication ID must be derived from the content-set SHA-256",
      });
    }
    const releaseIds = manifest.releaseLessonIds;
    const lessonIds = manifest.lessons.map((lesson) => lesson.lessonId);
    if (new Set(releaseIds).size !== releaseIds.length) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["releaseLessonIds"],
        message: "Release lesson IDs must be unique",
      });
    }
    if (new Set(lessonIds).size !== lessonIds.length) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["lessons"],
        message: "Publication lessons must be unique",
      });
    }
    if (
      [...releaseIds].sort().join("\n") !== releaseIds.join("\n") ||
      [...lessonIds].sort().join("\n") !== lessonIds.join("\n")
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["lessons"],
        message: "Publication lesson IDs must use deterministic sorted order",
      });
    }
    if (
      releaseIds.length !== lessonIds.length ||
      releaseIds.some((lessonId, index) => lessonId !== lessonIds[index])
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["releaseLessonIds"],
        message: "Release lesson IDs must exactly match publication lessons",
      });
    }
    for (const [index, lesson] of manifest.lessons.entries()) {
      const artifactPaths = lesson.reviewArtifacts.map(
        (artifact) => artifact.path,
      );
      if (new Set(artifactPaths).size !== artifactPaths.length) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["lessons", index, "reviewArtifacts"],
          message: "Review artifact paths must be unique per lesson",
        });
      }
      if (
        [...artifactPaths].sort().join("\n") !== artifactPaths.join("\n")
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["lessons", index, "reviewArtifacts"],
          message: "Review artifacts must use deterministic sorted order",
        });
      }
    }
    if (manifest.releaseChannel === "commercial") {
      if (
        manifest.reviewAuthority !== "dual_human" ||
        manifest.commercialReuseAllowed !== true ||
        manifest.requiresHumanCanonicalApprovalForCommercial !== false ||
        manifest.reviewAuthorityArtifact !== null
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["releaseChannel"],
          message: "Commercial publication requires dual human review",
        });
      }
    } else if (
      manifest.reviewAuthority !== "ai_pro_owner_authorized" ||
      manifest.commercialReuseAllowed !== false ||
      manifest.requiresHumanCanonicalApprovalForCommercial !== true ||
      manifest.reviewAuthorityArtifact === null
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["releaseChannel"],
        message:
          "Household publication requires a noncommercial hash-bound Pro authority",
      });
    }
  });

export type CanonicalPublicationLesson = z.infer<
  typeof CanonicalPublicationLessonSchema
>;
export type CanonicalPublicationManifest = z.infer<
  typeof CanonicalPublicationManifestSchema
>;

export interface CanonicalPublicationReleaseBoundary {
  releaseChannel: "commercial" | "household_noncommercial";
  reviewAuthority: "dual_human" | "ai_pro_owner_authorized";
  commercialReuseAllowed: boolean;
  requiresHumanCanonicalApprovalForCommercial: boolean;
  reviewAuthorityArtifact: CanonicalPublicationManifest["reviewAuthorityArtifact"];
}

export function canonicalPublicationContentString(
  lessons: CanonicalPublicationLesson[],
  boundary?: CanonicalPublicationReleaseBoundary,
): string {
  if (!boundary || boundary.releaseChannel === "commercial") {
    return JSON.stringify(lessons);
  }
  return JSON.stringify({ releaseBoundary: boundary, lessons });
}

export function canonicalPublicationReleaseBoundary(
  manifest: CanonicalPublicationManifest,
): CanonicalPublicationReleaseBoundary {
  return {
    releaseChannel: manifest.releaseChannel,
    reviewAuthority: manifest.reviewAuthority,
    commercialReuseAllowed: manifest.commercialReuseAllowed,
    requiresHumanCanonicalApprovalForCommercial:
      manifest.requiresHumanCanonicalApprovalForCommercial,
    reviewAuthorityArtifact: manifest.reviewAuthorityArtifact,
  };
}
